import React from "react";
import { VIDEO_FONT_FAMILY } from "../RegisterVideoFonts";
import { formatAmountOrUnlimited, formatMoney, formatPrice, pnlAtPrice } from "./scenarioExplainerTimeline";

/**
 * Payoff diagram drawn from engine data: expiration curve (+ optional T+0),
 * zero line, breakeven and max-loss markers, and a price marker.
 *
 * Plain SVG (not recharts) so every element is positioned in exact pixels and
 * the draw-in is a deterministic left-to-right clip reveal driven by `progress`.
 */
function niceStep(range, targetTicks) {
  const raw = range / Math.max(1, targetTicks);
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const candidates = [1, 2, 2.5, 5, 10].map((m) => m * pow);
  return candidates.find((c) => c >= raw) || candidates[candidates.length - 1];
}

function buildPath(series, x, y) {
  return series
    .map((p, i) => `${i === 0 ? "M" : "L"}${x(p.underlyingPrice).toFixed(2)},${y(p.pnl).toFixed(2)}`)
    .join(" ");
}

function Pill({ x, y, text, fill, color, fontSize, anchor = "middle" }) {
  const padX = fontSize * 0.55;
  const w = text.length * fontSize * 0.58 + padX * 2;
  const h = fontSize * 1.55;
  const left = anchor === "middle" ? x - w / 2 : anchor === "end" ? x - w : x;
  return (
    <g>
      <rect x={left} y={y - h / 2} width={w} height={h} rx={h / 2} fill={fill} />
      <text
        x={left + w / 2}
        y={y + fontSize * 0.36}
        textAnchor="middle"
        fontSize={fontSize}
        fontWeight={800}
        fill={color}
        fontFamily={VIDEO_FONT_FAMILY}
      >
        {text}
      </text>
    </g>
  );
}

export function PayoffChart({
  idPrefix = "payoff",
  width,
  height,
  expiration,
  tPlus0 = null,
  breakevens = [],
  maxLoss,
  ticker = "",
  markerPrice = null,
  progress = 1,
  markersOpacity = 1,
  markerOpacity = 1,
  colors,
  fontSize = 24,
}) {
  const padL = fontSize * 0.8;
  const padR = fontSize * 0.8;
  const padT = fontSize * 2.6;
  const padB = fontSize * 2.4;
  const plotW = Math.max(10, width - padL - padR);
  const plotH = Math.max(10, height - padT - padB);

  const all = [...expiration, ...(tPlus0 || [])];
  const minX = expiration[0].underlyingPrice;
  const maxX = expiration[expiration.length - 1].underlyingPrice;
  const rawMinY = Math.min(0, ...all.map((p) => p.pnl));
  const rawMaxY = Math.max(0, ...all.map((p) => p.pnl));
  const spanY = rawMaxY - rawMinY || 1;
  const minY = rawMinY - spanY * 0.2;
  const maxY = rawMaxY + spanY * 0.1;

  const x = (price) => padL + ((price - minX) / (maxX - minX)) * plotW;
  const y = (pnl) => padT + (1 - (pnl - minY) / (maxY - minY)) * plotH;
  const zeroY = y(0);
  const revealW = Math.max(0, Math.min(1, progress)) * plotW;

  const expPath = buildPath(expiration, x, y);
  const areaPath = `${expPath} L${x(maxX).toFixed(2)},${zeroY.toFixed(2)} L${x(minX).toFixed(2)},${zeroY.toFixed(2)} Z`;
  const t0Path = tPlus0 ? buildPath(tPlus0, x, y) : null;

  const step = niceStep(maxX - minX, 4);
  const ticks = [];
  for (let v = Math.ceil(minX / step) * step; v <= maxX + 1e-9; v += step) ticks.push(v);

  const visibleBreakevens = breakevens.filter((be) => be >= minX && be <= maxX);
  const lossValue = maxLoss === "Unlimited" ? null : -Math.abs(maxLoss);
  const showMaxLoss = lossValue != null && lossValue >= minY && lossValue < 0;

  const markerInRange = markerPrice != null && markerPrice >= minX && markerPrice <= maxX;
  const mx = markerInRange ? x(markerPrice) : 0;
  const expPnl = markerInRange ? pnlAtPrice(expiration, markerPrice) : 0;
  const t0Pnl = markerInRange && tPlus0 ? pnlAtPrice(tPlus0, markerPrice) : null;
  const font = VIDEO_FONT_FAMILY;
  const ids = {
    reveal: `${idPrefix}-reveal`,
    above: `${idPrefix}-above`,
    below: `${idPrefix}-below`,
  };

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block" }}>
      <defs>
        <clipPath id={ids.reveal}>
          <rect x={padL} y={0} width={revealW} height={height} />
        </clipPath>
        <clipPath id={ids.above}>
          <rect x={0} y={0} width={width} height={Math.max(0, zeroY)} />
        </clipPath>
        <clipPath id={ids.below}>
          <rect x={0} y={zeroY} width={width} height={Math.max(0, height - zeroY)} />
        </clipPath>
      </defs>

      <rect x={0} y={0} width={width} height={height} rx={fontSize * 1.1} fill={colors.surface} />

      {/* x-axis ticks */}
      {ticks.map((v) => (
        <g key={`tick-${v}`}>
          <line x1={x(v)} x2={x(v)} y1={padT} y2={padT + plotH} stroke={colors.muted} strokeOpacity={0.12} strokeWidth={2} />
          <text
            x={x(v)}
            y={padT + plotH + fontSize * 1.5}
            textAnchor={x(v) > padL + plotW - fontSize * 2 ? "end" : x(v) < padL + fontSize * 2 ? "start" : "middle"}
            fontSize={fontSize}
            fontWeight={600}
            fill={colors.muted}
            fontFamily={font}
          >
            {formatMoney(v)}
          </text>
        </g>
      ))}

      {/* zero line */}
      <line x1={padL} x2={padL + plotW} y1={zeroY} y2={zeroY} stroke={colors.muted} strokeOpacity={0.55} strokeWidth={3} />
      <text x={padL + fontSize * 0.4} y={zeroY - fontSize * 0.5} fontSize={fontSize * 0.9} fontWeight={700} fill={colors.muted} fontFamily={font}>
        $0
      </text>

      <g clipPath={`url(#${ids.reveal})`}>
        <path d={areaPath} fill={colors.accent} fillOpacity={0.2} clipPath={`url(#${ids.above})`} />
        <path d={areaPath} fill={colors.negative} fillOpacity={0.2} clipPath={`url(#${ids.below})`} />
        {t0Path ? (
          <path
            d={t0Path}
            fill="none"
            stroke={colors.text}
            strokeOpacity={0.85}
            strokeWidth={fontSize * 0.16}
            strokeDasharray={`${fontSize * 0.5} ${fontSize * 0.4}`}
            strokeLinejoin="round"
          />
        ) : null}
        <path d={expPath} fill="none" stroke={colors.accent} strokeWidth={fontSize * 0.26} strokeLinejoin="round" strokeLinecap="round" />
      </g>

      <g opacity={markersOpacity}>
        {showMaxLoss ? (
          <g>
            <line
              x1={padL}
              x2={padL + plotW}
              y1={y(lossValue)}
              y2={y(lossValue)}
              stroke={colors.negative}
              strokeWidth={3}
              strokeDasharray="10 8"
            />
            <text
              x={padL + plotW - fontSize * 0.4}
              y={y(lossValue) + fontSize * 1.35}
              textAnchor="end"
              fontSize={fontSize}
              fontWeight={800}
              fill={colors.negative}
              fontFamily={font}
            >
              {`Max loss ${formatAmountOrUnlimited(maxLoss, { loss: true })}`}
            </text>
          </g>
        ) : null}
        {visibleBreakevens.map((be) => (
          <line
            key={`be-${be}`}
            x1={x(be)}
            x2={x(be)}
            y1={padT}
            y2={padT + plotH}
            stroke={colors.accent}
            strokeWidth={3}
            strokeDasharray="8 8"
          />
        ))}
      </g>

      {markerInRange ? (
        <g opacity={markerOpacity}>
          <line x1={mx} x2={mx} y1={padT - fontSize * 0.4} y2={padT + plotH} stroke={colors.text} strokeWidth={3} />
          {t0Pnl != null ? <circle cx={mx} cy={y(t0Pnl)} r={fontSize * 0.36} fill={colors.text} /> : null}
          <circle cx={mx} cy={y(expPnl)} r={fontSize * 0.46} fill={colors.accent} stroke={colors.background} strokeWidth={4} />
          <Pill
            x={Math.min(Math.max(mx, padL + fontSize * 3.6), padL + plotW - fontSize * 3.6)}
            y={padT - fontSize * 1.3}
            text={`${ticker} ${formatPrice(markerPrice)}`}
            fill={colors.text}
            color={colors.background}
            fontSize={fontSize * 0.9}
          />
        </g>
      ) : null}

      {/* Breakeven labels last so the price marker never covers them. */}
      <g opacity={markersOpacity}>
        {visibleBreakevens.map((be) => (
          <Pill
            key={`be-label-${be}`}
            x={Math.min(Math.max(x(be), padL + fontSize * 4), padL + plotW - fontSize * 4)}
            y={padT + fontSize * 1.1}
            text={`Breakeven ${formatPrice(be)}`}
            fill={colors.background}
            color={colors.accent}
            fontSize={fontSize * 0.85}
          />
        ))}
      </g>
    </svg>
  );
}

export function PayoffLegend({ hasTPlus0, colors, fontSize }) {
  const item = (swatch, label) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: fontSize * 0.4, marginRight: fontSize * 1.2 }}>
      {swatch}
      <span>{label}</span>
    </span>
  );
  return (
    <div style={{ fontSize, fontWeight: 600, color: colors.muted, fontFamily: VIDEO_FONT_FAMILY, display: "flex", flexWrap: "wrap" }}>
      {item(<span style={{ width: fontSize * 1.6, height: fontSize * 0.26, borderRadius: 4, background: colors.accent }} />, "At expiration")}
      {hasTPlus0
        ? item(
            <span style={{ width: fontSize * 1.6, height: 0, borderTop: `${Math.max(2, fontSize * 0.14)}px dashed ${colors.text}` }} />,
            "Today (modeled)"
          )
        : null}
    </div>
  );
}
